import lodash from 'lodash';
import { nodeWithArgs } from 'common/util/queryHelper';
import { reportTypes } from './constants';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import styles from '../containers/ReportContent.scss';

import {
    binderReportDefaultColumns,
    binderReportColumnsList,
    getBindersReportNode,
    renderBindersColumns,
    reportBinderOptionData,
} from './binderUtils';
import {
    documentReportColumnsList,
    documentReportDefaultColumns,
    getDocumentsReportNode,
    reportDocumentOptionData,
    documentReportColumns,
    formatMaterialTableDocumentsColumns
} from './documentUtils';
import {
    getTasksReportNode,
    reportTaskOptionData,
    taskReportColumnsList,
    taskReportDefaultColumns,
    taskReportColumns,
    formatMaterialTableTasksColumns
} from './taskUtils';

import {
    expiringDocumentReportColumnsList,
    expiringDocumentReportDefaultColumns,
    getExpiringDocumentsReportNode,
    renderExpiringDocumentsColumns,
    reportExpiringDocumentsOptionData,
} from './expiringDocumentUtil';
import {
    getUnfiledDocumentsReportNode,
    renderUnfiledDocumentsColumns,
    reportUnfiledDocumentOptionData,
    unfiledDocumentReportColumnsList,
    unfiledDocumentReportDefaultColumns,
} from './unfiledDocumentUtils';
import {
    getUserDocumentAuditTrailNode,
    reportUserDocumentAuditTrailOptionData,
    userDocumentAuditTrailReportColumnsList,
    userDocumentAuditTrailReportDefaultColumns,
    userDocumentAuditTrailReportColumns,
    formatMaterialTableUserDocumentAuditTrailColumns
} from './userDocumentAuditTrailUtils';

import {
    getBillableHistoryReportNode,
    reportAuditHistoryOptionData,
    billableHistoryReportColumnList,
    billableHistoryReportDefaultColumns,
    billableHistoryReportColumns,
    formatMaterialTableBillableHistoryColumns
} from './auditHistoryUtils';

export function buildReportQuery(reportType, args = {}) {
    switch (reportType) {
        case reportTypes.binder:
            return getBindersReportNode(args.institutionId, ['active', 'archived', 'new']);
        case reportTypes.document:
            return getDocumentsReportNode(args);
        case reportTypes.task:
            return getTasksReportNode(args);
        case reportTypes.expiringDocument:
            return getExpiringDocumentsReportNode(args.institutionId);
        case reportTypes.unfiledDocument:
            return getUnfiledDocumentsReportNode(args.institutionId);
        case reportTypes.userDocumentAuditTrail:
            return getUserDocumentAuditTrailNode(args);
        case reportTypes.billableHistory:
            return getBillableHistoryReportNode(args.institutionId);
        default:
            return {};
    }
}

export function buildGetCustomizeColumnsQuery(reportType, institutionId) {
    return nodeWithArgs('getCustomizeColumns', {
        reportType,
        institutionId
    }, [
        'id',
        'reportType',
        'userId',
        'columnNames'
    ]);
}

export function buildSaveUserReportCustomizeColumnsQuery(reportType, columnNames, institutionId) {
    return nodeWithArgs('saveUserReportCustomizeColumns', { input: { reportType, columnNames }, institutionId }, [
        'id',
        'reportType',
        'userId',
        'columnNames',
    ]);
}

export const getReportColumns = reportType => {
    switch (reportType) {
        case reportTypes.binder:
            return binderReportColumnsList;
        case reportTypes.document:
            return documentReportColumnsList;
        case reportTypes.task:
            return taskReportColumnsList;
        case reportTypes.expiringDocument:
            return expiringDocumentReportColumnsList;
        case reportTypes.unfiledDocument:
            return unfiledDocumentReportColumnsList;
        case reportTypes.userDocumentAuditTrail:
            return userDocumentAuditTrailReportColumnsList;
        case reportTypes.billableHistory:
            return billableHistoryReportColumnList;
        default:
            return [];
    }
};

export const getReportDefaultColumns = reportType => {
    switch (reportType) {
        case reportTypes.binder:
            return binderReportDefaultColumns;
        case reportTypes.document:
            return documentReportDefaultColumns;
        case reportTypes.task:
            return taskReportDefaultColumns;
        case reportTypes.expiringDocument:
            return expiringDocumentReportDefaultColumns;
        case reportTypes.unfiledDocument:
            return unfiledDocumentReportDefaultColumns;
        case reportTypes.userDocumentAuditTrail:
            return userDocumentAuditTrailReportDefaultColumns;
        case reportTypes.billableHistory:
            return billableHistoryReportDefaultColumns;
        default:
            return [];
    }
};

export const renderReportColumns = (reportType, reportData, customizeColumns) => {
    switch (reportType) {
        case reportTypes.binder:
            return renderBindersColumns(reportData, customizeColumns);
        case reportTypes.expiringDocument:
            return renderExpiringDocumentsColumns(reportData, customizeColumns);
        case reportTypes.unfiledDocument:
            return renderUnfiledDocumentsColumns(reportData, customizeColumns);
        default:
            return customizeColumns;
    }
};

export const reportOptionData = [
    reportBinderOptionData,
    reportDocumentOptionData,
    reportTaskOptionData,
    reportExpiringDocumentsOptionData,
    reportUnfiledDocumentOptionData,
    reportUserDocumentAuditTrailOptionData,
    reportAuditHistoryOptionData
];

const EmptyRecordComponent = () => (
    <h1 className={styles.emptyDataSourceMessage}>No records to display</h1>
);

const LoadingDataSpinner = () => {
    return <div className={styles.emptyDataSourceMessage}>
        <LoadingStatus actionLabel={'loading'} itemName={''} loading={true} filterQuery={false} />
    </div>;
};

export const customLocalization = (isLoading) => {
    return {
        body: {
            emptyDataSourceMessage: isLoading ? <LoadingDataSpinner /> : <EmptyRecordComponent />,
        }
    };
};

const defaultColumns = {
    [reportTypes.document]: documentReportColumns,
    [reportTypes.task]: taskReportColumns,
    [reportTypes.userDocumentAuditTrail]: userDocumentAuditTrailReportColumns,
    [reportTypes.billableHistory]: billableHistoryReportColumns
};

export const isDynamicTableEnabled = {
    [reportTypes.binder]: false,
    [reportTypes.document]: true,
    [reportTypes.expiringDocument]: false,
    [reportTypes.unfiledDocument]: false,
    [reportTypes.task]: true,
    [reportTypes.userDocumentAuditTrail]: true,
    [reportTypes.billableHistory]: true
};

const getTableColumns = (reportType, customizeColumns, filters, institutionTimezone) => {
    switch (reportType) {
        case reportTypes.document:
            return formatMaterialTableDocumentsColumns(customizeColumns, filters);
        case reportTypes.task:
            return formatMaterialTableTasksColumns(customizeColumns, filters);
        case reportTypes.userDocumentAuditTrail:
            return formatMaterialTableUserDocumentAuditTrailColumns(customizeColumns, filters);
        case reportTypes.billableHistory:
            return formatMaterialTableBillableHistoryColumns(customizeColumns);
        default:
            return {};
    }
};

export const reportDefaultColumns = (reportType) => lodash.map(defaultColumns[reportType], 'key');

export function renderColumns(reportType, customizeColumns, filters = {}) {
    const { sortField, sortType } = filters;
    const formattedColumns = getTableColumns(reportType, customizeColumns, filters.filters);
    if (!lodash.isEmpty(sortField)) {
        const findColumn = lodash.find(formattedColumns, { field: sortField });
        if (!lodash.isEmpty(findColumn)) {
            findColumn.defaultSort = sortType;
        }
    }
    return [...formattedColumns];
}